export type Language = 'ja' | 'en';

type TranslationValue = { ja: string; en: string };
type TranslationSection = { [key: string]: TranslationValue };

export const translations: { [section: string]: TranslationSection } = {
  common: {
    loading: { ja: '読み込み中...', en: 'Loading...' },
    save: { ja: '保存', en: 'Save' },
    cancel: { ja: 'キャンセル', en: 'Cancel' },
    delete: { ja: '削除', en: 'Delete' },
    create: { ja: '作成', en: 'Create' },
    edit: { ja: '編集', en: 'Edit' },
    close: { ja: '閉じる', en: 'Close' },
    add: { ja: '追加', en: 'Add' },
    back: { ja: '← 戻る', en: '← Back' },
    email: { ja: 'メールアドレス', en: 'Email' },
    name: { ja: '名前', en: 'Name' },
    status: { ja: 'ステータス', en: 'Status' },
    error: { ja: 'エラー', en: 'Error' },
    saving: { ja: '保存中...', en: 'Saving...' },
    creating: { ja: '作成中...', en: 'Creating...' },
    deleteFailed: { ja: '削除に失敗しました', en: 'Failed to delete' },
    createdDate: { ja: '作成日:', en: 'Created:' },
    people: { ja: '人', en: '' },
    questions: { ja: '問', en: 'questions' },
    categories: { ja: 'カテゴリ', en: 'categories' },
    saved: { ja: '保存しました', en: 'Saved' },
    saveFailed: { ja: '保存に失敗しました', en: 'Failed to save' },
    loadFailed: { ja: '読み込みに失敗しました', en: 'Failed to load' },
  },

  header: {
    title: { ja: 'EA-QA システム', en: 'EA-QA System' },
    customers: { ja: 'お客様一覧', en: 'Customers' },
    questionnaires: { ja: '質問票', en: 'Questionnaires' },
    surveys: { ja: 'アンケート', en: 'Surveys' },
    templates: { ja: 'テンプレート', en: 'Templates' },
    emailGroups: { ja: 'メールグループ', en: 'Email Groups' },
    logout: { ja: 'ログアウト', en: 'Logout' },
    user: { ja: 'ユーザー', en: 'User' },
  },

  auth: {
    authenticating: { ja: '認証中...', en: 'Authenticating...' },
    redirecting: { ja: 'ログインにリダイレクト中...', en: 'Redirecting to login...' },
  },

  login: {
    title: { ja: 'EA-QA システム', en: 'EA-QA System' },
    email: { ja: 'メールアドレス', en: 'Email' },
    password: { ja: 'パスワード', en: 'Password' },
    loginButton: { ja: 'ログイン', en: 'Login' },
    loggingIn: { ja: 'ログイン中...', en: 'Logging in...' },
    loginFailed: { ja: 'ログインに失敗しました', en: 'Login failed' },
    loginTimeout: { ja: 'ログインがタイムアウトしました。ネットワーク接続を確認してください。', en: 'Login timed out. Please check your network connection.' },
  },

  customer: {
    title: { ja: 'お客様一覧', en: 'Customer List' },
    newCustomer: { ja: '新規お客様登録', en: 'New Customer Registration' },
    customerNamePlaceholder: { ja: 'お客様名を入力', en: 'Enter customer name' },
    emailPlaceholder: { ja: 'メールアドレス（アンケート送信用）', en: 'Email (for survey delivery)' },
    createNew: { ja: '新規作成', en: 'Create New' },
    existingCustomers: { ja: '既存のお客様', en: 'Existing Customers' },
    noCustomers: { ja: 'お客様が登録されていません', en: 'No customers registered' },
    deleteConfirm: { ja: 'を削除しますか？関連する回答データも全て削除されます。', en: '? All related response data will also be deleted.' },
    deleteFailed: { ja: 'お客様の削除に失敗しました', en: 'Failed to delete customer' },
    edit: { ja: '編集', en: 'Edit' },
    editCustomer: { ja: 'お客様情報編集', en: 'Edit Customer' },
    editSave: { ja: '保存', en: 'Save' },
    editCancel: { ja: 'キャンセル', en: 'Cancel' },
    editSuccess: { ja: 'お客様情報を更新しました', en: 'Customer updated successfully' },
    editFailed: { ja: 'お客様情報の更新に失敗しました', en: 'Failed to update customer' },
    archive: { ja: 'アーカイブ', en: 'Archive' },
    archiveConfirm: { ja: 'をアーカイブしますか？', en: '? Customer will be moved to archive.' },
    archiveFailed: { ja: 'アーカイブに失敗しました', en: 'Failed to archive customer' },
    restore: { ja: '復元', en: 'Restore' },
    restoreConfirm: { ja: 'を復元しますか？', en: '? Customer will be restored from archive.' },
    restoreFailed: { ja: '復元に失敗しました', en: 'Failed to restore customer' },
    archivedCustomers: { ja: 'アーカイブ済みのお客様', en: 'Archived Customers' },
    showArchived: { ja: 'アーカイブを表示', en: 'Show Archived' },
    hideArchived: { ja: 'アーカイブを非表示', en: 'Hide Archived' },
    archivedDate: { ja: 'アーカイブ日:', en: 'Archived:' },
    noArchivedCustomers: { ja: 'アーカイブ済みのお客様はいません', en: 'No archived customers' },
  },

  questionnaireList: {
    backToCustomers: { ja: '← お客様選択に戻る', en: '← Back to Customers' },
    management: { ja: '質問票管理', en: 'Questionnaire Management' },
    recipientEmail: { ja: '送信先メール:', en: 'Recipient Email:' },
    emailPlaceholder: { ja: 'メールアドレスを入力', en: 'Enter email address' },
    notSet: { ja: '未設定', en: 'Not set' },
    change: { ja: '変更', en: 'Change' },
    set: { ja: '設定', en: 'Set' },
    available: { ja: '利用可能な質問票', en: 'Available Questionnaires' },
    startNew: { ja: '新規回答を開始', en: 'Start New Response' },
    sending: { ja: '送信中...', en: 'Sending...' },
    sendByEmail: { ja: 'メールで送信', en: 'Send by Email' },
    responseHistory: { ja: '過去の回答履歴', en: 'Response History' },
    noResponses: { ja: '回答履歴がありません', en: 'No response history' },
    questionnaire: { ja: '質問票:', en: 'Questionnaire:' },
    answered: { ja: '回答済み', en: 'answered' },
    emailNotSet: { ja: 'メールアドレスが設定されていません。先にメールアドレスを設定してください。', en: 'Email address is not set. Please set the email address first.' },
    sendConfirm: { ja: 'に送信しますか？', en: '?' },
    sendFailed: { ja: 'メール送信に失敗しました', en: 'Failed to send email' },
    emailSaved: { ja: 'メールアドレスを保存しました', en: 'Email address saved' },
    emailSaveFailed: { ja: 'メールアドレスの保存に失敗しました', en: 'Failed to save email address' },
  },

  questionnaire: {
    lastSaved: { ja: '最終保存:', en: 'Last saved:' },
    progress: { ja: '回答進捗', en: 'Response Progress' },
    defaultAnswer: { ja: '回答例:', en: 'Example:' },
    answerPlaceholder: { ja: '回答を入力してください...', en: 'Enter your answer...' },
    aiReformat: { ja: 'AIで日本語を整形', en: 'AI Reformat' },
    aiSuggesting: { ja: '提案を取得中...', en: 'Getting suggestions...' },
    aiSuggestions: { ja: 'AI提案（クリックで採用）:', en: 'AI Suggestions (click to apply):' },
    notes: { ja: '補足:', en: 'Notes:' },
    saveAndComplete: { ja: '保存して完了', en: 'Save & Complete' },
    saved: { ja: '保存しました', en: 'Saved successfully' },
    saveFailed: { ja: '保存に失敗しました', en: 'Failed to save' },
  },

  sectionComments: {
    title: { ja: 'セクションコメント', en: 'Section Comments' },
    tabComments: { ja: 'コメント', en: 'Comments' },
    tabNotes: { ja: 'ノート', en: 'Notes' },
    tabAi: { ja: 'AI提案', en: 'AI Suggestions' },
    addComment: { ja: 'コメントを追加', en: 'Add Comment' },
    textPlaceholder: { ja: 'コメントを入力...', en: 'Enter comment...' },
    type: { ja: 'タイプ', en: 'Type' },
    typeComment: { ja: 'コメント', en: 'Comment' },
    typeNote: { ja: 'ノート', en: 'Note' },
    typeSuggestion: { ja: '提案', en: 'Suggestion' },
    priority: { ja: '優先度', en: 'Priority' },
    priorityHigh: { ja: '高', en: 'High' },
    priorityMedium: { ja: '中', en: 'Medium' },
    priorityLow: { ja: '低', en: 'Low' },
    priorityNone: { ja: 'なし', en: 'None' },
    tags: { ja: 'タグ', en: 'Tags' },
    tagsPlaceholder: { ja: 'カンマ区切りでタグを入力', en: 'Enter comma-separated tags' },
    attachImage: { ja: '画像添付', en: 'Attach Image' },
    addUrl: { ja: 'URL追加', en: 'Add URL' },
    urlPlaceholder: { ja: 'URLを入力', en: 'Enter URL' },
    urlTitlePlaceholder: { ja: 'リンクタイトル（任意）', en: 'Link title (optional)' },
    submit: { ja: '送信', en: 'Submit' },
    cancel: { ja: 'キャンセル', en: 'Cancel' },
    delete: { ja: '削除', en: 'Delete' },
    noComments: { ja: 'コメントはまだありません', en: 'No comments yet' },
    noNotes: { ja: 'ノートはまだありません', en: 'No notes yet' },
    generateAi: { ja: 'AI提案を生成', en: 'Generate AI Suggestions' },
    regenerateAi: { ja: '再生成', en: 'Regenerate' },
    aiGenerating: { ja: 'AI提案を生成中...', en: 'Generating AI suggestions...' },
    noAiSuggestions: { ja: 'まだAI提案がありません。「AI提案を生成」をクリックしてください。', en: 'No AI suggestions yet. Click "Generate AI Suggestions" to get started.' },
    acceptSuggestion: { ja: '採用', en: 'Accept' },
    accepted: { ja: '採用済み', en: 'Accepted' },
    aiGenerated: { ja: 'AI生成', en: 'AI Generated' },
    recommendation: { ja: '推奨', en: 'Recommendation' },
    observation: { ja: '観察', en: 'Observation' },
    risk: { ja: 'リスク', en: 'Risk' },
    uploading: { ja: 'アップロード中...', en: 'Uploading...' },
    commentCount: { ja: 'コメント', en: 'comments' },
  },

  customerManagement: {
    title: { ja: 'お客様一覧', en: 'Customer List' },
    createNew: { ja: '+ 新規作成', en: '+ Create New' },
    active: { ja: 'アクティブ', en: 'Active' },
    archived: { ja: 'アーカイブ済み', en: 'Archived' },
    noCustomers: { ja: 'お客様が登録されていません', en: 'No customers registered' },
    createPrompt: { ja: '「新規作成」ボタンからお客様を登録してください', en: 'Click "Create New" to register a customer' },
    customerName: { ja: 'お客様名', en: 'Customer Name' },
    customerNamePlaceholder: { ja: '例: ABC株式会社', en: 'e.g. ABC Corporation' },
    projectName: { ja: 'プロジェクト名', en: 'Project Name' },
    projectNamePlaceholder: { ja: '例: EA現状調査プロジェクト', en: 'e.g. EA Assessment Project' },
    emailPlaceholder: { ja: 'メールアドレス（任意）', en: 'Email (optional)' },
    createFailed: { ja: 'お客様の作成に失敗しました', en: 'Failed to create customer' },
    linkedSurveys: { ja: 'リンク済みアンケート', en: 'Linked Surveys' },
    addSurvey: { ja: 'アンケートを追加', en: 'Add Survey' },
    noLinkedSurveys: { ja: 'リンクされたアンケートはありません', en: 'No linked surveys' },
    selectSurvey: { ja: 'アンケートを選択', en: 'Select Survey' },
    noAvailableSurveys: { ja: '追加可能なアンケートがありません', en: 'No available surveys to add' },
    unlink: { ja: '解除', en: 'Unlink' },
    linkFailed: { ja: 'アンケートのリンクに失敗しました', en: 'Failed to link survey' },
    unlinkFailed: { ja: 'アンケートの解除に失敗しました', en: 'Failed to unlink survey' },
    deleteConfirmTitle: { ja: 'お客様を削除', en: 'Delete Customer' },
    deleteConfirmMessage: { ja: 'このお客様を削除しますか？関連するデータも全て削除されます。', en: 'Delete this customer? All related data will also be deleted.' },
  },

  survey: {
    management: { ja: 'アンケート管理', en: 'Survey Management' },
    templateManagement: { ja: 'テンプレート管理', en: 'Template Management' },
    createNew: { ja: '新規作成', en: 'Create New' },
    noSurveys: { ja: 'アンケートがありません', en: 'No surveys found' },
    createPrompt: { ja: '「新規作成」ボタンからアンケートを作成してください', en: 'Create a survey using the "Create New" button' },
    statusDraft: { ja: '下書き', en: 'Draft' },
    statusActive: { ja: '実施中', en: 'Active' },
    statusClosed: { ja: '終了', en: 'Closed' },
    responses: { ja: '回答:', en: 'Responses:' },
    deadline: { ja: '期限:', en: 'Deadline:' },
    created: { ja: '作成:', en: 'Created:' },
    respondentCount: { ja: '人', en: '' },
  },

  surveyCreate: {
    title: { ja: 'アンケート新規作成', en: 'Create New Survey' },
    template: { ja: 'テンプレート', en: 'Template' },
    noTemplates: { ja: 'テンプレートがありません。先に', en: 'No templates available. Please ' },
    uploadTemplateLink: { ja: 'テンプレートをアップロード', en: 'upload a template' },
    noTemplatesSuffix: { ja: 'してください。', en: ' first.' },
    selectTemplate: { ja: 'テンプレートを選択...', en: 'Select a template...' },
    surveyTitle: { ja: 'アンケートタイトル', en: 'Survey Title' },
    titlePlaceholder: { ja: '例: ABC社 現状確認アンケート', en: 'e.g. Company ABC Current State Survey' },
    deadline: { ja: '回答期限', en: 'Response Deadline' },
    respondents: { ja: '回答者', en: 'Respondents' },
    clear: { ja: 'クリア', en: 'Clear' },
    individual: { ja: '個別入力', en: 'Individual' },
    bulk: { ja: '一括入力', en: 'Bulk Input' },
    fromGroup: { ja: 'グループから', en: 'From Group' },
    csvImport: { ja: 'CSVインポート', en: 'CSV Import' },
    addRespondent: { ja: '+ 回答者を追加', en: '+ Add Respondent' },
    bulkPlaceholder: { ja: 'メールアドレス,名前（1行に1人ずつ）\n例:\ntanaka@example.com,田中太郎\nsuzuki@example.com,鈴木花子', en: 'email,name (one per line)\ne.g.:\njohn@example.com,John Smith\njane@example.com,Jane Doe' },
    noGroups: { ja: 'メールグループがありません。', en: 'No email groups available. ' },
    groupManagementLink: { ja: 'グループ管理', en: 'Group Management' },
    noGroupsSuffix: { ja: 'からグループを作成してください。', en: ' to create a group.' },
    csvDescription: { ja: 'CSVファイル（メールアドレス,名前）を選択してください。ヘッダー行は自動的にスキップされます。', en: 'Select a CSV file (email, name). Header rows are automatically skipped.' },
    createSurvey: { ja: 'アンケートを作成', en: 'Create Survey' },
    templateAndTitleRequired: { ja: 'テンプレートとタイトルを指定してください', en: 'Please specify a template and title' },
    respondentRequired: { ja: '少なくとも1人の回答者を指定してください', en: 'Please specify at least one respondent' },
    createFailed: { ja: '作成に失敗しました: ', en: 'Creation failed: ' },
    groupLoadFailed: { ja: 'グループの読み込みに失敗しました', en: 'Failed to load group' },
    noValidEmails: { ja: '有効なメールアドレスが見つかりませんでした', en: 'No valid email addresses found' },
  },

  surveyDetail: {
    backToList: { ja: '← 一覧に戻る', en: '← Back to List' },
    notFound: { ja: 'アンケートが見つかりません', en: 'Survey not found' },
    sendEmail: { ja: 'メール送信', en: 'Send Email' },
    close: { ja: '終了', en: 'Close' },
    viewResults: { ja: '結果を見る', en: 'View Results' },
    respondentList: { ja: '回答者一覧', en: 'Respondent List' },
    url: { ja: 'URL', en: 'URL' },
    statusPending: { ja: '未送信', en: 'Pending' },
    statusSent: { ja: '送信済', en: 'Sent' },
    statusInProgress: { ja: '回答中', en: 'In Progress' },
    statusCompleted: { ja: '完了', en: 'Completed' },
    copyUrl: { ja: 'URLコピー', en: 'Copy URL' },
    urlCopied: { ja: 'URLをコピーしました', en: 'URL copied' },
    sendConfirm: { ja: '回答者にメールを送信しますか？（ローカル環境ではコンソールにログ出力されます）', en: 'Send emails to respondents? (In local env, output is logged to console)' },
    sendSuccess: { ja: '送信しました（ローカル環境：コンソールログを確認してください）', en: 'Sent successfully (Local env: check console logs)' },
    sendFailed: { ja: '送信に失敗しました: ', en: 'Failed to send: ' },
    closeConfirm: { ja: 'このアンケートを終了しますか？回答者は以降回答できなくなります。', en: 'Close this survey? Respondents will no longer be able to answer.' },
    closeFailed: { ja: '終了に失敗しました', en: 'Failed to close' },
  },

  surveyResults: {
    backToDetail: { ja: '← アンケート詳細に戻る', en: '← Back to Survey Detail' },
    dataNotFound: { ja: 'データが見つかりません', en: 'Data not found' },
    results: { ja: '結果', en: 'Results' },
    compare: { ja: '比較表示', en: 'Compare' },
    allExcel: { ja: '全回答Excel', en: 'All Responses Excel' },
    individualExcel: { ja: '個別Excel', en: 'Individual Excel' },
    totalRespondents: { ja: '回答者数', en: 'Respondents' },
    completed: { ja: '完了', en: 'Completed' },
    inProgress: { ja: '回答中', en: 'In Progress' },
    avgCompletionRate: { ja: '平均回答率', en: 'Avg. Completion' },
    summaryTab: { ja: '集計', en: 'Summary' },
    individualTab: { ja: '個別回答', en: 'Individual' },
    questionStats: { ja: '質問別回答状況', en: 'Response Status by Question' },
    answeredCount: { ja: '人回答', en: ' answered' },
    selectRespondent: { ja: '回答者を選択...', en: 'Select respondent...' },
    exportThis: { ja: 'この回答をExport', en: 'Export this response' },
    completionRate: { ja: '回答率:', en: 'Completion:' },
    statusLabel: { ja: 'ステータス:', en: 'Status:' },
    submitted: { ja: '提出:', en: 'Submitted:' },
    unanswered: { ja: '（未回答）', en: '(Unanswered)' },
    remarks: { ja: '備考:', en: 'Notes:' },
    selectPrompt: { ja: '回答者を選択してください', en: 'Please select a respondent' },
  },

  surveyCompare: {
    backToResults: { ja: '← 結果に戻る', en: '← Back to Results' },
    comparison: { ja: '回答比較', en: 'Response Comparison' },
    no: { ja: 'No', en: 'No' },
    category: { ja: 'カテゴリ', en: 'Category' },
    question: { ja: '質問', en: 'Question' },
  },

  template: {
    management: { ja: 'テンプレート管理', en: 'Template Management' },
    csvUpload: { ja: 'CSVアップロード', en: 'CSV Upload' },
    noTemplates: { ja: 'テンプレートがありません', en: 'No templates' },
    uploadPrompt: { ja: 'CSVファイルをアップロードしてテンプレートを作成してください', en: 'Upload a CSV file to create a template' },
    typeCurrentState: { ja: '現状確認', en: 'Current State' },
    typeFutureVision: { ja: '将来像', en: 'Future Vision' },
    typeMaturity: { ja: '成熟度評価', en: 'Maturity Assessment' },
    typeGapAnalysis: { ja: 'ギャップ分析', en: 'Gap Analysis' },
    deleteConfirm: { ja: 'を削除しますか？', en: '?' },
    goToSurveys: { ja: 'アンケート管理へ', en: 'Go to Surveys' },
  },

  templateUpload: {
    title: { ja: 'テンプレートアップロード', en: 'Template Upload' },
    titleLabel: { ja: 'タイトル', en: 'Title' },
    titlePlaceholder: { ja: '例: 現状確認アンケート', en: 'e.g. Current State Survey' },
    typeLabel: { ja: 'タイプ', en: 'Type' },
    customType: { ja: 'カスタムタイプ', en: 'Custom Type' },
    customTypePlaceholder: { ja: 'カスタムタイプ名を入力', en: 'Enter custom type name' },
    fileLabel: { ja: 'ファイル（CSV / Excel）', en: 'File (CSV / Excel)' },
    filePrompt: { ja: 'クリックしてCSVまたはExcelファイルを選択', en: 'Click to select a CSV or Excel file' },
    headerNote: { ja: 'ヘッダー行: カテゴリ, 質問, 目的, 回答例, 補足 (英語ヘッダーにも対応)', en: 'Header row: Category, Question, Purpose, Example Answer, Notes (English headers also supported)' },
    sheetSelection: { ja: 'シート選択', en: 'Sheet Selection' },
    preview: { ja: 'プレビュー (先頭10行)', en: 'Preview (first 10 rows)' },
    uploading: { ja: 'アップロード中...', en: 'Uploading...' },
    upload: { ja: 'アップロード', en: 'Upload' },
    titleAndFileRequired: { ja: 'タイトルとファイルを指定してください', en: 'Please specify a title and file' },
    typeRequired: { ja: 'タイプを指定してください', en: 'Please specify a type' },
    uploadFailed: { ja: 'アップロードに失敗しました: ', en: 'Upload failed: ' },
  },

  templateEditor: {
    title: { ja: 'テンプレート編集', en: 'Edit Template' },
    basicInfo: { ja: '基本情報', en: 'Basic Information' },
    questions: { ja: '質問一覧', en: 'Questions' },
    addQuestion: { ja: '質問を追加', en: 'Add Question' },
    noQuestions: { ja: '質問がありません。「質問を追加」をクリックして追加してください。', en: 'No questions. Click "Add Question" to add one.' },
    emptyQuestion: { ja: '（質問文を入力）', en: '(Enter question text)' },
    category: { ja: 'カテゴリ', en: 'Category' },
    questionText: { ja: '質問文', en: 'Question Text' },
    purpose: { ja: '目的', en: 'Purpose' },
    defaultAnswer: { ja: '回答例', en: 'Example Answer' },
    notes: { ja: '補足', en: 'Notes' },
    moveUp: { ja: '上へ', en: 'Up' },
    moveDown: { ja: '下へ', en: 'Down' },
    deleteQuestionConfirm: { ja: 'この質問を削除しますか？', en: 'Delete this question?' },
    titleRequired: { ja: 'タイトルを入力してください', en: 'Please enter a title' },
    questionsRequired: { ja: '少なくとも1つの質問を追加してください', en: 'Please add at least one question' },
    cannotEdit: { ja: 'このテンプレートは編集できません', en: 'This template cannot be edited' },
    notFound: { ja: 'テンプレートが見つかりません', en: 'Template not found' },
  },

  emailGroup: {
    management: { ja: 'メールグループ管理', en: 'Email Group Management' },
    newGroup: { ja: '+ 新規グループ', en: '+ New Group' },
    createGroup: { ja: '新規グループ作成', en: 'Create New Group' },
    groupName: { ja: 'グループ名', en: 'Group Name' },
    descriptionPlaceholder: { ja: '説明（任意）', en: 'Description (optional)' },
    nameRequired: { ja: 'グループ名を入力してください', en: 'Please enter a group name' },
    createFailed: { ja: 'グループの作成に失敗しました', en: 'Failed to create group' },
    deleteConfirm: { ja: 'このグループを削除しますか？', en: 'Delete this group?' },
    noGroups: { ja: 'グループがありません', en: 'No groups' },
    addMember: { ja: 'メンバー追加', en: 'Add Member' },
    csvBulkAdd: { ja: 'CSVで一括追加', en: 'Bulk add via CSV' },
    members: { ja: 'メンバー', en: 'Members' },
    noMembers: { ja: 'メンバーがいません', en: 'No members' },
    addMemberFailed: { ja: 'メンバーの追加に失敗しました', en: 'Failed to add member' },
    removeMemberFailed: { ja: 'メンバーの削除に失敗しました', en: 'Failed to remove member' },
    csvImportFailed: { ja: 'CSVインポートに失敗しました', en: 'CSV import failed' },
    testEmail: { ja: 'テスト', en: 'Test' },
    testing: { ja: '送信中...', en: 'Sending...' },
    testSuccess: { ja: 'テストメールを送信しました', en: 'Test email sent successfully' },
    testFailed: { ja: 'テストメール送信に失敗しました', en: 'Failed to send test email' },
  },

  surveyPublic: {
    respondent: { ja: '回答者:', en: 'Respondent:' },
    progress: { ja: '進捗:', en: 'Progress:' },
    lastSaved: { ja: '最終保存:', en: 'Last saved:' },
    category: { ja: 'カテゴリ', en: 'Categories' },
    defaultAnswer: { ja: '回答例:', en: 'Example:' },
    answerPlaceholder: { ja: '回答を入力してください', en: 'Enter your answer' },
    notesPlaceholder: { ja: '備考（任意）', en: 'Notes (optional)' },
    tempSave: { ja: '一時保存', en: 'Save Draft' },
    submitting: { ja: '提出中...', en: 'Submitting...' },
    submit: { ja: '回答を提出する', en: 'Submit Response' },
    loadFailed: { ja: 'アンケートの読み込みに失敗しました', en: 'Failed to load survey' },
    unansweredConfirm: { ja: '件あります。このまま提出しますか？', en: ' unanswered questions. Submit anyway?' },
    unansweredPrefix: { ja: '未回答の質問が', en: 'There are ' },
    saveFailed: { ja: '保存に失敗しました: ', en: 'Failed to save: ' },
    submitFailed: { ja: '提出に失敗しました: ', en: 'Failed to submit: ' },
  },

  surveyComplete: {
    title: { ja: '回答を受け付けました', en: 'Response Received' },
    thankYou: { ja: 'アンケートへのご協力ありがとうございました。', en: 'Thank you for your cooperation.' },
    submitted: { ja: 'ご回答は正常に送信されました。', en: 'Your response has been successfully submitted.' },
    canClose: { ja: 'このページは閉じていただいて構いません。', en: 'You may close this page.' },
  },

  surveyExpired: {
    title: { ja: 'リンクが無効です', en: 'Link Invalid' },
    message: { ja: 'このアンケートリンクは期限切れまたは無効です。', en: 'This survey link has expired or is invalid.' },
    contact: { ja: '管理者にお問い合わせください。', en: 'Please contact the administrator.' },
  },
};
